import { z } from 'zod';
import { AddressSchema } from './address.js';

/**
 * Detected invoice format. Mirrors ADR-0002 stage 2 outcomes plus the
 * `xml-unknown` bucket for XML files we cannot dispatch to a CII or UBL parser.
 */
export const InvoiceFormatEnum = z.enum([
  'zugferd-minimum',
  'zugferd-basic',
  'zugferd-basic-wl',
  'zugferd-en16931',
  'zugferd-extended',
  'xrechnung-cii',
  'xrechnung-ubl',
  'pdf-llm',
  'xml-unknown',
]);
export type InvoiceFormat = z.infer<typeof InvoiceFormatEnum>;

/**
 * Lifecycle states a single invoice can be in. ADR-0002 stage 4 sets
 * `manual_review` when validation/plausibility checks fail.
 */
export const InvoiceStatusEnum = z.enum([
  'queued',
  'processing',
  'success',
  'posted',
  'manual_review',
  'error',
]);
export type InvoiceStatus = z.infer<typeof InvoiceStatusEnum>;

export const ExtractionMethodEnum = z.enum(['structured', 'llm']);
export type ExtractionMethod = z.infer<typeof ExtractionMethodEnum>;

export const InvoiceDocumentTypeEnum = z.enum(['invoice', 'order_confirmation', 'customer_order']);
export type InvoiceDocumentType = z.infer<typeof InvoiceDocumentTypeEnum>;
export const LineItemTypeEnum = z.enum(['standard', 'ancillary_cost']);
export type LineItemType = z.infer<typeof LineItemTypeEnum>;
const ORDER_CONFIRMATION_PAYMENT_TERM_DAYS = 30;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

const KNOWN_LOEFFELHARDT_SUPPLIER_NAME = 'emil loffelhardt gmbh co kg';
const KNOWN_LOEFFELHARDT_VAT_ID = 'DE147223734';
const KNOWN_LOEFFELHARDT_BANK_ACCOUNTS = [
  {
    bankName: 'Kreissparkasse Waiblingen',
    iban: 'DE91602500100015055150',
    bic: 'SOLADES1WBN',
  },
  {
    bankName: 'Volksbank Mittlerer Neckar eG',
    iban: 'DE26612901200502471000',
    bic: 'GENODES1NUE',
  },
  {
    bankName: 'Deutsche Bank',
    iban: 'DE50600700700160460200',
    bic: 'DEUTDESSXXX',
  },
  {
    bankName: 'BW-Bank Stuttgart',
    iban: 'DE25600501010002255729',
    bic: 'SOLADEST600',
  },
  {
    bankName: 'Hypovereinsbank Stuttgart',
    iban: 'DE62600202900001047914',
    bic: 'HYVEDEMM473',
  },
  {
    bankName: 'Volksbank Stuttgart eG',
    iban: 'DE54600901000648590003',
    bic: 'VOBADESSXXX',
  },
] as const;

function normaliseComparableName(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/&/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

function normaliseCountry(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (trimmed.length === 0) return undefined;
  if (/^[A-Za-z]{2}$/.test(trimmed)) return trimmed.toUpperCase();

  const key = trimmed.toLowerCase();
  switch (key) {
    case 'deutschland':
    case 'germany':
      return 'DE';
    case 'österreich':
    case 'oesterreich':
    case 'austria':
      return 'AT';
    case 'schweiz':
    case 'switzerland':
      return 'CH';
    default:
      return undefined;
  }
}

function inferCountryFromPostalCode(postalCode: string | undefined): string | undefined {
  if (!postalCode) return undefined;
  return /^\d{5}$/.test(postalCode) ? 'DE' : undefined;
}

function extractCountryHint(party: Record<string, unknown>): string | undefined {
  const address = party.address;
  if (isRecord(address) && typeof address.country === 'string') {
    return normaliseCountry(address.country);
  }
  if (typeof party.vatId === 'string' && /^[A-Za-z]{2}/.test(party.vatId)) {
    return party.vatId.slice(0, 2).toUpperCase();
  }
  return undefined;
}

function normaliseAddressValue(
  value: unknown,
  countryHint: string | undefined,
): { address: unknown; addressText?: string } {
  if (typeof value === 'string') {
    const addressText = value.trim();
    const parts = addressText
      .split(',')
      .map((part) => part.trim())
      .filter((part) => part.length > 0);

    let remaining = [...parts];
    let country = countryHint;

    const explicitCountry = normaliseCountry(remaining.at(-1));
    if (explicitCountry) {
      country = explicitCountry;
      remaining = remaining.slice(0, -1);
    }

    const parsedAddress: Record<string, string> = {};
    const postalCity = remaining.at(-1);
    const match = postalCity ? /^(\d{4,6})\s+(.+)$/.exec(postalCity) : null;
    if (match) {
      parsedAddress.postalCode = match[1] ?? '';
      parsedAddress.city = match[2] ?? '';
      remaining = remaining.slice(0, -1);
      country ??= inferCountryFromPostalCode(parsedAddress.postalCode);
    }

    if (!match) {
      const collapsed = addressText.replace(/\s+/g, ' ').trim();
      const inlineGermanPostalMatches = Array.from(
        collapsed.matchAll(/\b(?:D[-\s])?(\d{5})\s+([^,\d]+?)(?=$|,)/g),
      );
      const lastInlineGermanPostalMatch = inlineGermanPostalMatches.at(-1);
      if (lastInlineGermanPostalMatch?.index !== undefined) {
        parsedAddress.postalCode = lastInlineGermanPostalMatch[1] ?? '';
        parsedAddress.city = lastInlineGermanPostalMatch[2]?.trim() ?? '';
        const streetSource = collapsed.slice(0, lastInlineGermanPostalMatch.index).trim();
        const streetParts = streetSource
          .split(/[\n,]+|\s{2,}/)
          .map((part) => part.trim())
          .filter((part) => part.length > 0);
        const street =
          pickInlineStreetFromText(streetSource) ??
          streetParts
            .slice()
            .reverse()
            .find((part) =>
              /(?:str\.?|strasse|straße|weg|allee|platz|gasse|ring|damm|ufer|chaussee|steig)\b|\d+\s*[a-z]?(?:\s*[-/]\s*\d+\s*[a-z]?)?$/i.test(
                part,
              ),
            ) ??
          streetSource;
        parsedAddress.street = street;
        country ??= inferCountryFromPostalCode(parsedAddress.postalCode);
        remaining = [];
      }
      const inlineMatch = /^(.*\S)\s+(\d{4,6})\s+(.+)$/.exec(collapsed);
      if (inlineMatch && !parsedAddress.postalCode) {
        parsedAddress.street = inlineMatch[1] ?? '';
        parsedAddress.postalCode = inlineMatch[2] ?? '';
        parsedAddress.city = inlineMatch[3] ?? '';
        country ??= inferCountryFromPostalCode(parsedAddress.postalCode);
        remaining = [];
      }
    }

    const street = remaining.join(', ');
    if (street.length > 0) parsedAddress.street = street;
    if (country) parsedAddress.country = country;

    return { address: parsedAddress, addressText };
  }

  if (isRecord(value)) {
    const address = { ...value };
    if (typeof address.country === 'string') {
      const normalisedCountry = normaliseCountry(address.country);
      if (normalisedCountry) address.country = normalisedCountry;
    } else if (countryHint) {
      address.country = countryHint;
    }
    return { address };
  }

  return { address: value };
}

function normalisePartyInput(value: unknown): unknown {
  if (!isRecord(value)) return value;
  const party = { ...value };
  const countryHint = extractCountryHint(party);
  const normalisedAddress = normaliseAddressValue(party.address, countryHint);
  party.address = normalisedAddress.address;
  if (normalisedAddress.addressText && typeof party.addressText !== 'string') {
    party.addressText = normalisedAddress.addressText;
  }
  if (typeof party.vatId === 'string') {
    party.vatId = party.vatId.replace(/\s+/g, '');
  }
  return party;
}

function enrichKnownSupplier(value: unknown): unknown {
  if (!isRecord(value) || typeof value.name !== 'string') return value;
  if (normaliseComparableName(value.name) !== KNOWN_LOEFFELHARDT_SUPPLIER_NAME) {
    return value;
  }

  const firstBankAccount = KNOWN_LOEFFELHARDT_BANK_ACCOUNTS[0];
  const seller: Record<string, unknown> = {
    ...value,
    vatId: KNOWN_LOEFFELHARDT_VAT_ID,
    bankAccounts: KNOWN_LOEFFELHARDT_BANK_ACCOUNTS.map((account) => ({ ...account })),
    iban: firstBankAccount?.iban,
    bic: firstBankAccount?.bic,
  };
  seller.taxId = undefined;
  return seller;
}

function mergePartyInput(aliasValue: unknown, canonicalValue: unknown): unknown {
  if (isRecord(aliasValue) && isRecord(canonicalValue)) {
    return { ...aliasValue, ...canonicalValue };
  }
  return canonicalValue ?? aliasValue;
}

function normaliseVatRate(value: unknown): unknown {
  if (typeof value !== 'number' || !Number.isFinite(value)) return value;
  if (value > 0 && value <= 1) return value * 100;
  return value;
}

function normaliseAncillaryCostText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

export function isAncillaryCostLineDescription(description: string | undefined): boolean {
  if (!description) return false;
  const text = normaliseAncillaryCostText(description);
  return /\b(fracht|frachtkosten|freight|lieferkosten|lieferkostenpauschale|delivery costs?|shipping|versand|versandkosten|porto|paketdienst|transport|verpackung|verpackungskosten|packaging|packing|handling|zuschlag|surcharge|sonderkosten|nebenkosten|service fees?|servicepauschale|bearbeitungsgebuhr)\b/i.test(
    text,
  );
}

function normaliseLineItemTypeValue(value: unknown): LineItemType | undefined {
  if (typeof value !== 'string') return undefined;
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  switch (normalized) {
    case 'standard':
    case 'regular':
    case 'normal':
      return 'standard';
    case 'ancillary_cost':
    case 'ancillary':
    case 'cost':
    case 'cost_line':
    case 'nebenkosten':
    case 'nebenkostenposition':
    case 'nebenkosten_position':
      return 'ancillary_cost';
    default:
      return undefined;
  }
}

function normaliseDocumentTypeValue(value: unknown): InvoiceDocumentType | undefined {
  if (typeof value !== 'string') return undefined;
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

  switch (normalized) {
    case 'invoice':
    case 'rechnung':
      return 'invoice';
    case 'order_confirmation':
    case 'orderconfirmation':
    case 'auftragsbestatigung':
    case 'auftragsbestaetigung':
    case 'auftragsbestätigung':
      return 'order_confirmation';
    case 'customer_order':
    case 'customerorder':
    case 'customer_purchase_order':
    case 'kundenbestellung':
    case 'kunden_bestellung':
    case 'kundenauftrag':
    case 'kunden_auftrag':
      return 'customer_order';
    default:
      return undefined;
  }
}

function inferDocumentTypeFromSource(value: Record<string, unknown>): InvoiceDocumentType {
  const explicit =
    normaliseDocumentTypeValue(value.documentType) ??
    (isRecord(value.raw) ? normaliseDocumentTypeValue(value.raw.documentType) : undefined);
  if (explicit) return explicit;

  const searchTerms = [
    value.paymentTerms,
    isRecord(value.invoice) ? value.invoice.number : undefined,
    isRecord(value.raw) ? value.raw.title : undefined,
    isRecord(value.raw) ? value.raw.subject : undefined,
  ]
    .filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
    .map((entry) => entry.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase());

  if (
    searchTerms.some(
      (entry) => entry.includes('auftragsbestatigung') || entry.includes('order confirmation'),
    )
  ) {
    return 'order_confirmation';
  }

  if (
    searchTerms.some(
      (entry) =>
        entry.includes('kundenbestellung') ||
        entry.includes('customer order') ||
        entry.includes('purchase order from customer'),
    )
  ) {
    return 'customer_order';
  }

  return 'invoice';
}

function isIsoDateString(value: unknown): value is string {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function parseLocaleNumber(value: string): number | undefined {
  const trimmed = value.trim();
  if (trimmed.length === 0) return undefined;

  let normalised = trimmed.replace(/\s+/g, '');
  if (normalised.includes(',') && normalised.includes('.')) {
    normalised =
      normalised.lastIndexOf(',') > normalised.lastIndexOf('.')
        ? normalised.replace(/\./g, '').replace(',', '.')
        : normalised.replace(/,/g, '');
  } else if (normalised.includes(',')) {
    normalised = normalised.replace(',', '.');
  }

  const parsed = Number.parseFloat(normalised);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function roundCurrency(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function formatIsoDateParts(year: number, month: number, day: number): string | undefined {
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return undefined;
  }

  const parsed = new Date(Date.UTC(year, month - 1, day));
  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  ) {
    return undefined;
  }

  return `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-${day
    .toString()
    .padStart(2, '0')}`;
}

function expandTwoDigitYear(year: number): number {
  return year >= 70 ? 1900 + year : 2000 + year;
}

function getIsoWeeksInYear(year: number): number {
  const dec28 = new Date(Date.UTC(year, 11, 28));
  const day = dec28.getUTCDay() || 7;
  const thursday = new Date(dec28);
  thursday.setUTCDate(dec28.getUTCDate() + (4 - day));
  const yearStart = new Date(Date.UTC(thursday.getUTCFullYear(), 0, 1));
  return Math.ceil(((thursday.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
}

function formatIsoWeekStartDate(year: number, week: number): string | undefined {
  if (!Number.isInteger(year) || !Number.isInteger(week) || week < 1) return undefined;
  if (week > getIsoWeeksInYear(year)) return undefined;

  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Day = jan4.getUTCDay() || 7;
  const monday = new Date(jan4);
  monday.setUTCDate(jan4.getUTCDate() - jan4Day + 1 + (week - 1) * 7);
  return monday.toISOString().slice(0, 10);
}

function extractCalendarWeekDate(value: string, fallbackYear?: number): string | undefined {
  const normalised = value.replace(/\s+/g, ' ').trim();
  const weekMatch =
    /(?:^|[^A-Za-z0-9])KW\s*([0-5]?\d)(?:\s*[\/.-]\s*(\d{2,4}))?/i.exec(normalised) ??
    /(?:^|[^A-Za-z0-9])LW\s*([0-5]?\d)(?:\s*[\/.-]\s*(\d{2,4}))?/i.exec(normalised) ??
    /(?:lieferwoche|lieferkw)\s*([0-5]?\d)(?:\s*[\/.-]\s*(\d{2,4}))?/i.exec(normalised) ??
    /^([0-5]?\d)\s*[/.-]\s*(\d{2,4})$/i.exec(normalised) ??
    /(?:calendar\s*week|cw)\s*([0-5]?\d)(?:\s*[\/.-]\s*(\d{2,4}))?/i.exec(normalised);
  if (!weekMatch) return undefined;

  const week = Number.parseInt(weekMatch[1] ?? '', 10);
  const rawYear = weekMatch[2];
  const parsedYear = rawYear
    ? Number.parseInt(rawYear, 10)
    : Number.isInteger(fallbackYear)
      ? fallbackYear
      : undefined;
  if (!parsedYear || Number.isNaN(week) || Number.isNaN(parsedYear)) return undefined;

  const year = rawYear && rawYear.length === 2 ? expandTwoDigitYear(parsedYear) : parsedYear;
  return formatIsoWeekStartDate(year, week);
}

export function normaliseInvoiceDateValue(
  value: unknown,
  options: { fallbackYear?: number } = {},
): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (trimmed.length === 0) return undefined;

  if (isIsoDateString(trimmed)) return trimmed;

  const isoWithTime = /^(\d{4}-\d{2}-\d{2})(?:[T\s].*)?$/.exec(trimmed);
  if (isoWithTime?.[1]) return isoWithTime[1];

  const compactIso = /^(\d{4})(\d{2})(\d{2})$/.exec(trimmed);
  if (compactIso) {
    const [, year, month, day] = compactIso;
    return formatIsoDateParts(
      Number.parseInt(year ?? '', 10),
      Number.parseInt(month ?? '', 10),
      Number.parseInt(day ?? '', 10),
    );
  }

  const yearFirst = /^(\d{4})[./](\d{1,2})[./](\d{1,2})$/.exec(trimmed);
  if (yearFirst) {
    const [, year, month, day] = yearFirst;
    return formatIsoDateParts(
      Number.parseInt(year ?? '', 10),
      Number.parseInt(month ?? '', 10),
      Number.parseInt(day ?? '', 10),
    );
  }

  const dayFirst = /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/.exec(trimmed);
  if (dayFirst) {
    const [, day, month, year] = dayFirst;
    return formatIsoDateParts(
      Number.parseInt(year ?? '', 10),
      Number.parseInt(month ?? '', 10),
      Number.parseInt(day ?? '', 10),
    );
  }

  const dayFirstShortYear = /^(\d{1,2})[./-](\d{1,2})[./-](\d{2})$/.exec(trimmed);
  if (dayFirstShortYear) {
    const [, day, month, year] = dayFirstShortYear;
    return formatIsoDateParts(
      expandTwoDigitYear(Number.parseInt(year ?? '', 10)),
      Number.parseInt(month ?? '', 10),
      Number.parseInt(day ?? '', 10),
    );
  }

  const kwDate = extractCalendarWeekDate(trimmed, options.fallbackYear);
  if (kwDate) return kwDate;

  return undefined;
}

function normaliseOptionalDateValue(value: unknown, fallbackYear?: number): string | undefined {
  return fallbackYear === undefined
    ? normaliseInvoiceDateValue(value)
    : normaliseInvoiceDateValue(value, { fallbackYear });
}

function addDaysIso(date: string, days: number): string | undefined {
  const parsed = new Date(`${date}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) return undefined;
  parsed.setUTCDate(parsed.getUTCDate() + days);
  return parsed.toISOString().slice(0, 10);
}

interface PaymentTermsHints {
  skontoPercent?: number;
  skontoAmount?: number;
  skontoDeadline?: string;
  amountAfterSkonto?: number;
  withoutDeductionDueDate?: string;
  paymentDueDate?: string;
}

function parsePaymentTermsHints(
  paymentTerms: string | undefined,
  options: { baseDate?: string; fallbackYear?: number; grossAmount?: number } = {},
): PaymentTermsHints {
  if (!paymentTerms || paymentTerms.trim().length === 0) return {};

  const hints: PaymentTermsHints = {};
  const explicitDiscountAmountMatch =
    /skontobetrag\s+([0-9.,]+).*?(?:bis(?:\s+zum)?|bis)\s+(\d{1,2}[./-]\d{1,2}[./-]\d{2,4})/i.exec(
      paymentTerms,
    );
  if (explicitDiscountAmountMatch) {
    const amount = parseLocaleNumber(explicitDiscountAmountMatch[1] ?? '');
    const deadline = normaliseOptionalDateValue(
      explicitDiscountAmountMatch[2],
      options.fallbackYear,
    );
    if (amount !== undefined) hints.skontoAmount = roundCurrency(amount);
    if (deadline) hints.skontoDeadline = deadline;
  }

  const skontoMatch =
    /(?:innerhalb\s+)?(\d{1,3})\s*tage?n?\s+([0-9]+(?:[.,][0-9]+)?)\s*%?\s*skonto/i.exec(
      paymentTerms,
    ) ??
    /([0-9]+(?:[.,][0-9]+)?)\s*%?\s*skonto(?:\s+innerhalb)?\s+(\d{1,3})\s*tage?n?/i.exec(
      paymentTerms,
    );
  const skontoDays =
    skontoMatch?.index === undefined
      ? undefined
      : Number.parseInt(
          (skontoMatch[0]?.toLowerCase().includes('skonto innerhalb')
            ? skontoMatch[2]
            : skontoMatch[1]) ?? '',
          10,
        );
  const skontoPercent =
    skontoMatch?.index === undefined
      ? undefined
      : parseLocaleNumber(
          (skontoMatch[0]?.toLowerCase().includes('skonto innerhalb')
            ? skontoMatch[1]
            : skontoMatch[2]) ?? '',
        );

  if (skontoPercent !== undefined) hints.skontoPercent = skontoPercent;
  if (!hints.skontoDeadline && options.baseDate && skontoDays) {
    const derivedSkontoDeadline = addDaysIso(options.baseDate, skontoDays);
    if (derivedSkontoDeadline) hints.skontoDeadline = derivedSkontoDeadline;
  }

  const dueDaysMatch = /(?:innerhalb\s+)?(\d{1,3})\s*tage?n?\s*(?:ohne\s+abzug|netto)/i.exec(
    paymentTerms,
  );
  const dueDays = dueDaysMatch ? Number.parseInt(dueDaysMatch[1] ?? '', 10) : undefined;
  if (options.baseDate && dueDays) {
    const dueDate = addDaysIso(options.baseDate, dueDays);
    if (dueDate) {
      hints.withoutDeductionDueDate = dueDate;
      hints.paymentDueDate = dueDate;
    }
  }

  if (options.grossAmount !== undefined) {
    if (hints.skontoAmount === undefined && hints.skontoPercent !== undefined) {
      hints.skontoAmount = roundCurrency((options.grossAmount * hints.skontoPercent) / 100);
    }
    if (hints.amountAfterSkonto === undefined && hints.skontoAmount !== undefined) {
      hints.amountAfterSkonto = roundCurrency(options.grossAmount - hints.skontoAmount);
    }
    if (
      hints.skontoPercent === undefined &&
      hints.skontoAmount !== undefined &&
      options.grossAmount > 0
    ) {
      hints.skontoPercent = roundCurrency((hints.skontoAmount / options.grossAmount) * 100);
    }
  }

  return hints;
}

function pickInlineStreetFromText(value: string): string | undefined {
  const matches = Array.from(
    value.matchAll(
      /((?:[\p{L}\d.'-]+\s+){0,4}[\p{L}\d.'-]*(?:str\.?|strasse|straße|weg|allee|platz|gasse|ring|damm|ufer|chaussee|steig)\s+\d+\s*[a-z]?(?:\s*[-/]\s*\d+\s*[a-z]?)?)/giu,
    ),
  );
  const candidate = matches.at(-1)?.[1]?.trim();
  return candidate ? trimStreetCandidate(candidate) : undefined;
}

function trimStreetCandidate(value: string): string {
  const tokens = value.split(/\s+/).filter((token) => token.length > 0);
  let streetTokenIndex = -1;
  for (let index = tokens.length - 1; index >= 0; index -= 1) {
    if (
      /(?:str\.?|strasse|straße|weg|allee|platz|gasse|ring|damm|ufer|chaussee|steig)\b/iu.test(
        tokens[index] ?? '',
      )
    ) {
      streetTokenIndex = index;
      break;
    }
  }
  return streetTokenIndex >= 0 ? tokens.slice(streetTokenIndex).join(' ') : value.trim();
}

function deriveDueDate(
  source: Record<string, unknown>,
  documentType: InvoiceDocumentType,
): string | undefined {
  const invoice = isRecord(source.invoice) ? source.invoice : undefined;
  const raw = isRecord(source.raw) ? source.raw : undefined;
  const extractedAttributes =
    raw && isRecord(raw.extractedAttributes) ? raw.extractedAttributes : undefined;
  const fallbackYear =
    normaliseOptionalDateValue(invoice?.issueDate)?.slice(0, 4) !== undefined
      ? Number.parseInt(normaliseOptionalDateValue(invoice?.issueDate)?.slice(0, 4) ?? '', 10)
      : undefined;

  const explicitDueDate =
    normaliseOptionalDateValue(invoice?.dueDate, fallbackYear) ??
    normaliseOptionalDateValue(extractedAttributes?.withoutDeductionDueDate, fallbackYear) ??
    normaliseOptionalDateValue(extractedAttributes?.paymentDueDate, fallbackYear);
  if (explicitDueDate) return explicitDueDate;

  if (documentType !== 'order_confirmation') return undefined;

  const deliveryDate =
    normaliseOptionalDateValue(invoice?.deliveryDate, fallbackYear) ??
    normaliseOptionalDateValue(extractedAttributes?.deliveryDate, fallbackYear);
  if (!deliveryDate) return undefined;

  return addDaysIso(deliveryDate, ORDER_CONFIRMATION_PAYMENT_TERM_DAYS);
}

function normaliseReferenceArrays(
  attributes: Record<string, unknown>,
  fallbackYear?: number,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  if (Array.isArray(attributes.purchaseOrders)) {
    const purchaseOrders = attributes.purchaseOrders
      .map((entry) => {
        if (!isRecord(entry) || typeof entry.orderNumber !== 'string') return undefined;
        const orderNumber = entry.orderNumber.trim();
        if (orderNumber.length === 0) return undefined;
        return {
          orderNumber,
          ...(normaliseOptionalDateValue(entry.orderDate, fallbackYear)
            ? { orderDate: normaliseOptionalDateValue(entry.orderDate, fallbackYear) }
            : {}),
          ...(normaliseOptionalDateValue(entry.deliveryDate, fallbackYear)
            ? { deliveryDate: normaliseOptionalDateValue(entry.deliveryDate, fallbackYear) }
            : {}),
          ...(typeof entry.deliveryAddress === 'string' && entry.deliveryAddress.trim().length > 0
            ? { deliveryAddress: entry.deliveryAddress.trim() }
            : {}),
        };
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry !== undefined);
    if (purchaseOrders.length > 0) result.purchaseOrders = purchaseOrders;
  }

  if (Array.isArray(attributes.deliveryNotes)) {
    const deliveryNotes = attributes.deliveryNotes
      .map((entry) => {
        if (!isRecord(entry) || typeof entry.deliveryNote !== 'string') return undefined;
        const deliveryNote = entry.deliveryNote.trim();
        if (deliveryNote.length === 0) return undefined;
        return {
          deliveryNote,
          ...(typeof entry.orderNumber === 'string' && entry.orderNumber.trim().length > 0
            ? { orderNumber: entry.orderNumber.trim() }
            : {}),
          ...(normaliseOptionalDateValue(entry.deliveryDate, fallbackYear)
            ? { deliveryDate: normaliseOptionalDateValue(entry.deliveryDate, fallbackYear) }
            : {}),
          ...(typeof entry.deliveryAddress === 'string' && entry.deliveryAddress.trim().length > 0
            ? { deliveryAddress: entry.deliveryAddress.trim() }
            : {}),
          ...(typeof entry.shippingMethod === 'string' && entry.shippingMethod.trim().length > 0
            ? { shippingMethod: entry.shippingMethod.trim() }
            : {}),
        };
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry !== undefined);
    if (deliveryNotes.length > 0) result.deliveryNotes = deliveryNotes;
  }
  return result;
}

function normaliseInvoiceBlobInput(value: unknown): unknown {
  if (!isRecord(value)) return value;

  const source = { ...value };
  const invoice = isRecord(source.invoice) ? source.invoice : undefined;
  const totals = isRecord(source.totals) ? source.totals : undefined;
  const grossAmount =
    typeof totals?.grossAmount === 'number' && Number.isFinite(totals.grossAmount)
      ? totals.grossAmount
      : typeof totals?.netAmount === 'number' &&
          Number.isFinite(totals.netAmount) &&
          typeof totals?.vatAmount === 'number' &&
          Number.isFinite(totals.vatAmount)
        ? totals.netAmount + totals.vatAmount
        : undefined;
  source.seller = enrichKnownSupplier(
    normalisePartyInput(mergePartyInput(source.supplier, source.seller)),
  );
  source.buyer = normalisePartyInput(mergePartyInput(source.customer, source.buyer));
  const issueDate = normaliseOptionalDateValue(invoice?.issueDate);
  const referenceYear = issueDate ? Number.parseInt(issueDate.slice(0, 4), 10) : undefined;
  if (invoice) {
    const dueDate = normaliseOptionalDateValue(invoice.dueDate, referenceYear);
    const deliveryDate = normaliseOptionalDateValue(invoice.deliveryDate, referenceYear);
    source.invoice = {
      ...invoice,
      ...(issueDate ? { issueDate } : {}),
      ...(dueDate ? { dueDate } : {}),
      ...(deliveryDate ? { deliveryDate } : {}),
    };
  }
  if (Array.isArray(source.lineItems)) {
    source.lineItems = source.lineItems.map((lineItem) => {
      if (!isRecord(lineItem)) return lineItem;
      const description =
        (typeof lineItem.description === 'string' && lineItem.description.trim().length > 0
          ? lineItem.description.trim()
          : undefined) ??
        (typeof lineItem.name === 'string' && lineItem.name.trim().length > 0
          ? lineItem.name.trim()
          : undefined) ??
        (typeof lineItem.text === 'string' && lineItem.text.trim().length > 0
          ? lineItem.text.trim()
          : undefined);
      const articleNumber =
        (typeof lineItem.articleNumber === 'string' && lineItem.articleNumber.trim().length > 0
          ? lineItem.articleNumber.trim()
          : undefined) ??
        (typeof lineItem.materialNumber === 'string' && lineItem.materialNumber.trim().length > 0
          ? lineItem.materialNumber.trim()
          : undefined) ??
        (typeof lineItem.material === 'string' && lineItem.material.trim().length > 0
          ? lineItem.material.trim()
          : undefined) ??
        (typeof lineItem.partNumber === 'string' && lineItem.partNumber.trim().length > 0
          ? lineItem.partNumber.trim()
          : undefined) ??
        (typeof lineItem.productNumber === 'string' && lineItem.productNumber.trim().length > 0
          ? lineItem.productNumber.trim()
          : undefined);
      const customerMaterialNumber =
        (typeof lineItem.customerMaterialNumber === 'string' &&
        lineItem.customerMaterialNumber.trim().length > 0
          ? lineItem.customerMaterialNumber.trim()
          : undefined) ??
        (typeof lineItem.customerArticleNumber === 'string' &&
        lineItem.customerArticleNumber.trim().length > 0
          ? lineItem.customerArticleNumber.trim()
          : undefined);
      const supplierMaterialNumber =
        (typeof lineItem.supplierMaterialNumber === 'string' &&
        lineItem.supplierMaterialNumber.trim().length > 0
          ? lineItem.supplierMaterialNumber.trim()
          : undefined) ?? articleNumber;
      const orderDate = normaliseOptionalDateValue(lineItem.orderDate, referenceYear);
      const deliveryDate = normaliseOptionalDateValue(lineItem.deliveryDate, referenceYear);
      const netAmount =
        typeof lineItem.netAmount === 'number' && Number.isFinite(lineItem.netAmount)
          ? lineItem.netAmount
          : typeof lineItem.amount === 'number' && Number.isFinite(lineItem.amount)
            ? lineItem.amount
            : typeof lineItem.total === 'number' && Number.isFinite(lineItem.total)
              ? lineItem.total
              : undefined;
      const isCostLine = isAncillaryCostLineDescription(description);
      const itemType =
        normaliseLineItemTypeValue(lineItem.itemType) ??
        (isCostLine ? 'ancillary_cost' : 'standard');
      const quantity =
        typeof lineItem.quantity === 'number' && Number.isFinite(lineItem.quantity)
          ? lineItem.quantity
          : isCostLine && netAmount !== undefined
            ? 1
            : undefined;
      const unitPrice =
        typeof lineItem.unitPrice === 'number' && Number.isFinite(lineItem.unitPrice)
          ? lineItem.unitPrice
          : typeof lineItem.price === 'number' && Number.isFinite(lineItem.price)
            ? lineItem.price
            : quantity === 1 && netAmount !== undefined
              ? netAmount
              : undefined;
      return {
        ...lineItem,
        ...(description ? { description } : {}),
        ...(articleNumber ? { articleNumber } : {}),
        ...(supplierMaterialNumber ? { supplierMaterialNumber } : {}),
        ...(customerMaterialNumber ? { customerMaterialNumber } : {}),
        ...(orderDate ? { orderDate } : {}),
        ...(deliveryDate ? { deliveryDate } : {}),
        ...(quantity !== undefined ? { quantity } : {}),
        ...(unitPrice !== undefined ? { unitPrice } : {}),
        ...(netAmount !== undefined ? { netAmount } : {}),
        itemType,
        vatRate: normaliseVatRate(lineItem.vatRate),
      };
    });
  }
  if (isRecord(source.raw) && isRecord(source.raw.extractedAttributes)) {
    const {
      orderDate: _rawOrderDate,
      customerPoDate: _rawCustomerPoDate,
      deliveryDate: _rawDeliveryDate,
      requestedDeliveryDate: _rawRequestedDeliveryDate,
      vatRate: _rawVatRate,
      skontoDeadline: _rawSkontoDeadline,
      withoutDeductionDueDate: _rawWithoutDeductionDueDate,
      paymentDueDate: _rawPaymentDueDate,
      ...remainingExtractedAttributes
    } = source.raw.extractedAttributes;
    const orderDate =
      normaliseOptionalDateValue(source.raw.extractedAttributes.orderDate, referenceYear) ??
      normaliseOptionalDateValue(source.raw.extractedAttributes.customerPoDate, referenceYear) ??
      issueDate;
    const customerPoDate =
      normaliseOptionalDateValue(source.raw.extractedAttributes.customerPoDate, referenceYear) ??
      normaliseOptionalDateValue(source.raw.extractedAttributes.orderDate, referenceYear) ??
      issueDate;
    const paymentTermsHints = parsePaymentTermsHints(
      typeof source.paymentTerms === 'string' ? source.paymentTerms : undefined,
      {
        ...(issueDate ? { baseDate: issueDate } : {}),
        ...(referenceYear !== undefined ? { fallbackYear: referenceYear } : {}),
        ...(grossAmount !== undefined ? { grossAmount } : {}),
      },
    );
    const explicitSkontoAmount =
      typeof source.raw.extractedAttributes.skontoAmount === 'number' &&
      Number.isFinite(source.raw.extractedAttributes.skontoAmount)
        ? source.raw.extractedAttributes.skontoAmount
        : undefined;
    const explicitSkontoPercent =
      typeof source.raw.extractedAttributes.skontoPercent === 'number' &&
      Number.isFinite(source.raw.extractedAttributes.skontoPercent)
        ? source.raw.extractedAttributes.skontoPercent
        : undefined;
    const explicitAmountAfterSkonto =
      typeof source.raw.extractedAttributes.amountAfterSkonto === 'number' &&
      Number.isFinite(source.raw.extractedAttributes.amountAfterSkonto)
        ? source.raw.extractedAttributes.amountAfterSkonto
        : undefined;
    const skontoAmount =
      explicitSkontoAmount ??
      paymentTermsHints.skontoAmount ??
      (explicitSkontoPercent !== undefined && grossAmount !== undefined
        ? roundCurrency((grossAmount * explicitSkontoPercent) / 100)
        : undefined);
    const skontoPercent =
      explicitSkontoPercent ??
      paymentTermsHints.skontoPercent ??
      (skontoAmount !== undefined && grossAmount !== undefined && grossAmount > 0
        ? roundCurrency((skontoAmount / grossAmount) * 100)
        : undefined);
    const amountAfterSkonto =
      explicitAmountAfterSkonto ??
      paymentTermsHints.amountAfterSkonto ??
      (grossAmount !== undefined && skontoAmount !== undefined
        ? roundCurrency(grossAmount - skontoAmount)
        : undefined);
    const withoutDeductionDueDate =
      normaliseOptionalDateValue(
        source.raw.extractedAttributes.withoutDeductionDueDate,
        referenceYear,
      ) ?? paymentTermsHints.withoutDeductionDueDate;
    const paymentDueDate =
      normaliseOptionalDateValue(source.raw.extractedAttributes.paymentDueDate, referenceYear) ??
      paymentTermsHints.paymentDueDate ??
      withoutDeductionDueDate;
    source.raw = {
      ...source.raw,
      extractedAttributes: {
        ...remainingExtractedAttributes,
        ...normaliseReferenceArrays(source.raw.extractedAttributes, referenceYear),
        ...(orderDate ? { orderDate } : {}),
        ...(typeof source.raw.extractedAttributes.customerPoNumber === 'string' &&
        source.raw.extractedAttributes.customerPoNumber.trim().length > 0
          ? { customerPoNumber: source.raw.extractedAttributes.customerPoNumber.trim() }
          : {}),
        ...(customerPoDate ? { customerPoDate } : {}),
        deliveryDate: normaliseOptionalDateValue(
          source.raw.extractedAttributes.deliveryDate,
          referenceYear,
        ),
        requestedDeliveryDate: normaliseOptionalDateValue(
          source.raw.extractedAttributes.requestedDeliveryDate,
          referenceYear,
        ),
        vatRate: normaliseVatRate(source.raw.extractedAttributes.vatRate),
        ...((normaliseOptionalDateValue(
          source.raw.extractedAttributes.skontoDeadline,
          referenceYear,
        ) ?? paymentTermsHints.skontoDeadline)
          ? {
              skontoDeadline:
                normaliseOptionalDateValue(
                  source.raw.extractedAttributes.skontoDeadline,
                  referenceYear,
                ) ?? paymentTermsHints.skontoDeadline,
            }
          : {}),
        ...(skontoPercent !== undefined ? { skontoPercent } : {}),
        ...(skontoAmount !== undefined ? { skontoAmount } : {}),
        ...(amountAfterSkonto !== undefined ? { amountAfterSkonto } : {}),
        ...(withoutDeductionDueDate ? { withoutDeductionDueDate } : {}),
        ...(paymentDueDate ? { paymentDueDate } : {}),
      },
    };
  }
  if (!isRecord(source.meta)) {
    source.meta = {
      format: 'pdf-llm',
      extractionMethod: 'llm',
      extractedAt: '1970-01-01T00:00:00.000Z',
    };
  }
  const documentType = inferDocumentTypeFromSource(source);
  source.documentType = documentType;
  if (isRecord(source.invoice)) {
    source.invoice = {
      ...source.invoice,
      ...(deriveDueDate(source, documentType)
        ? { dueDate: deriveDueDate(source, documentType) }
        : {}),
    };
  }
  source.supplier = undefined;
  source.customer = undefined;

  return source;
}

export const SellerBankAccountSchema = z.object({
  bankName: z.string().min(1),
  iban: z.string().min(1),
  bic: z.string().min(1),
});
export type SellerBankAccount = z.infer<typeof SellerBankAccountSchema>;

export const SellerSchema = z.object({
  name: z.string().min(1),
  sapSupplierId: z.string().optional(),
  address: AddressSchema,
  addressText: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().email().optional(),
  website: z.string().url().optional(),
  vatId: z.string().optional(),
  taxId: z.string().optional(),
  iban: z.string().optional(),
  bic: z.string().optional(),
  bankAccounts: z.array(SellerBankAccountSchema).optional(),
});
export type Seller = z.infer<typeof SellerSchema>;

export const BuyerSchema = z.object({
  name: z.string().min(1),
  sapCustomerId: z.string().optional(),
  address: AddressSchema,
  addressText: z.string().optional(),
  customerNumber: z.string().optional(),
  vatId: z.string().optional(),
});
export type Buyer = z.infer<typeof BuyerSchema>;

export const LineItemSchema = z.object({
  position: z.string().optional(),
  itemType: LineItemTypeEnum.optional(),
  articleNumber: z.string().optional(),
  sapMaterialNumber: z.string().optional(),
  supplierMaterialNumber: z.string().optional(),
  customerMaterialNumber: z.string().optional(),
  customerArticleNumber: z.string().optional(),
  orderNumber: z.string().optional(),
  orderDate: z.string().date().optional(),
  deliveryNote: z.string().optional(),
  deliveryDate: z.string().date().optional(),
  deliveryAddress: z.string().optional(),
  description: z.string().min(1),
  quantity: z.number(),
  unit: z.string().optional(),
  unitPrice: z.number(),
  pricePer: z.number().optional(),
  marginPercent: z.number().optional(),
  marginAmount: z.number().optional(),
  discountPercent: z.number().optional(),
  discountAmount: z.number().optional(),
  netAmount: z.number(),
  vatRate: z.number(),
});
export type LineItem = z.infer<typeof LineItemSchema>;

export const TotalsSchema = z.object({
  netAmount: z.number(),
  vatAmount: z.number(),
  grossAmount: z.number(),
});
export type Totals = z.infer<typeof TotalsSchema>;

export const RawPurchaseOrderSchema = z.object({
  orderNumber: z.string(),
  orderDate: z.string().date().optional(),
  deliveryDate: z.string().date().optional(),
  deliveryAddress: z.string().optional(),
});
export type RawPurchaseOrder = z.infer<typeof RawPurchaseOrderSchema>;

export const RawDeliveryNoteSchema = z.object({
  deliveryNote: z.string(),
  orderNumber: z.string().optional(),
  deliveryDate: z.string().date().optional(),
  deliveryAddress: z.string().optional(),
  shippingMethod: z.string().optional(),
});
export type RawDeliveryNote = z.infer<typeof RawDeliveryNoteSchema>;

export const RawPdfChunkSchema = z.object({
  kind: z.enum(['header', 'positions', 'summary', 'appendix']),
  startPage: z.number().int().positive(),
  endPage: z.number().int().positive(),
  contextStartPage: z.number().int().positive().optional(),
  contextEndPage: z.number().int().positive().optional(),
});
export type RawPdfChunk = z.infer<typeof RawPdfChunkSchema>;

export const RawExtractedAttributesSchema = z.object({
  customerNumber: z.string().optional(),
  orderNumber: z.string().optional(),
  orderDate: z.string().date().optional(),
  customerPoNumber: z.string().optional(),
  customerPoDate: z.string().date().optional(),
  salesOrderType: z.string().optional(),
  salesOrganization: z.string().optional(),
  distributionChannel: z.string().optional(),
  division: z.string().optional(),
  shippingPlant: z.string().optional(),
  billingBlock: z.string().optional(),
  deliveryBlock: z.string().optional(),
  requestedDeliveryDate: z.string().date().optional(),
  purchaseOrders: z.array(RawPurchaseOrderSchema).optional(),
  orderConfirmationNumber: z.string().optional(),
  billingAddress: z.string().optional(),
  deliveryNote: z.string().optional(),
  deliveryNotes: z.array(RawDeliveryNoteSchema).optional(),
  deliveryDate: z.string().date().optional(),
  shippingMethod: z.string().optional(),
  deliveryAddress: z.string().optional(),
  incoterms: z.string().optional(),
  processedBy: z.string().optional(),
  processedByPhone: z.string().optional(),
  processedByEmail: z.string().email().optional(),
  vatBase: z.number().optional(),
  vatRate: z.number().optional(),
  vatAmount: z.number().optional(),
  skontoDeadline: z.string().date().optional(),
  skontoPercent: z.number().optional(),
  skontoAmount: z.number().optional(),
  amountAfterSkonto: z.number().optional(),
  withoutDeductionDueDate: z.string().date().optional(),
  paymentDueDate: z.string().date().optional(),
  registryCourt: z.string().optional(),
  generalPartner: z.string().optional(),
  managingDirectors: z.string().optional(),
  pdfChunks: z.array(RawPdfChunkSchema).optional(),
  missingFields: z.array(z.string()).optional(),
});
export type RawExtractedAttributes = z.infer<typeof RawExtractedAttributesSchema>;

export const RawInvoicePayloadSchema = z.object({
  source: z.string().optional(),
  documentType: z.string().optional(),
  title: z.string().optional(),
  subject: z.string().optional(),
  extractedAttributes: RawExtractedAttributesSchema.optional(),
});
export type RawInvoicePayload = z.infer<typeof RawInvoicePayloadSchema>;

export const InvoiceExtractionResponseJsonSchema = {
  type: 'object',
  properties: {
    documentType: {
      type: 'string',
      enum: ['invoice', 'order_confirmation', 'customer_order'],
      description:
        'Classify the document as invoice, order confirmation / Auftragsbestaetigung, or customer_order / Kundenbestellung.',
    },
    invoice: {
      type: 'object',
      properties: {
        number: {
          type: 'string',
          description:
            'Document number. Use the invoice number for invoices and the order confirmation number for order confirmations.',
        },
        issueDate: {
          type: 'string',
          description: 'Document date in YYYY-MM-DD when clearly shown.',
        },
        dueDate: { type: 'string', description: 'Payment due date in YYYY-MM-DD.' },
        deliveryDate: {
          type: 'string',
          description:
            'Delivery date / Lieferdatum in YYYY-MM-DD. If the document gives only a calendar week such as KW 35/2025, convert it to the Monday of that week.',
        },
        currency: { type: 'string', description: '3-letter ISO currency code, e.g. EUR.' },
      },
    },
    supplier: {
      type: 'object',
      properties: {
        name: { type: 'string', description: 'Supplier name / vendor issuing the invoice.' },
        sapSupplierId: {
          type: 'string',
          description:
            'SAP supplier/vendor ID. Usually server-side SAP enrichment; omit unless explicitly shown in the source document.',
        },
        address: {
          type: 'object',
          description: 'Structured supplier postal address.',
          properties: {
            street: { type: 'string', description: 'Supplier street and house number.' },
            postalCode: { type: 'string', description: 'Supplier postal code / PLZ.' },
            city: { type: 'string', description: 'Supplier city / Ort.' },
            country: { type: 'string', description: 'Supplier country as ISO-2 code.' },
          },
        },
        phone: { type: 'string', description: 'Supplier phone number.' },
        email: { type: 'string', description: 'Supplier email address.' },
        website: { type: 'string', description: 'Supplier website URL if present.' },
        vatId: { type: 'string', description: 'Supplier VAT ID / USt-IdNr.' },
      },
    },
    customer: {
      type: 'object',
      properties: {
        name: {
          type: 'string',
          description:
            'Invoice recipient / customer name. Prefer the shipping recipient / Warenempfaenger if a separate shipping address is shown. If the recipient is a person, keep that person or recipient block as the customer name and still extract the address fields.',
        },
        sapCustomerId: {
          type: 'string',
          description:
            'SAP customer/sold-to-party ID. Usually server-side SAP enrichment; omit unless explicitly shown in the source document.',
        },
        address: {
          type: 'object',
          description:
            'Structured customer postal address. Prefer Warenempfaenger / shipping/delivery address over billing address when both are present. If the document says Lieferanschrift siehe Rechnungsanschrift, resolve it to the printed invoice-recipient address. Do not use supplier footer/imprint addresses.',
          properties: {
            street: { type: 'string', description: 'Customer street and house number.' },
            postalCode: { type: 'string', description: 'Customer postal code / PLZ.' },
            city: { type: 'string', description: 'Customer city / Ort.' },
            country: { type: 'string', description: 'Customer country as ISO-2 code.' },
          },
        },
        customerNumber: { type: 'string', description: 'Customer number / Kundennummer.' },
        vatId: { type: 'string', description: 'Customer VAT ID / USt-IdNr.' },
      },
    },
    paymentTerms: {
      type: 'string',
      description:
        'Complete payment terms text, preserving skonto conditions, deadlines, exclusions, and punctuation.',
    },
    lineItems: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          position: { type: 'string', description: 'Line item position / Pos.' },
          itemType: {
            type: 'string',
            enum: ['standard', 'ancillary_cost'],
            description:
              'Classify as `ancillary_cost` for freight, packing, handling, Verpackungskosten, Versandkosten, Fracht, Lieferkosten, Sonderkosten, Nebenkosten, service fees, surcharge, and similar amount-contributing cost rows. Use `standard` for normal product or service positions.',
          },
          articleNumber: {
            type: 'string',
            description:
              'Article, item, material, or product number. Also use this for Materialnummer, Artnr, Part No., or Product No. Omit when the document does not show a reliable identifier.',
          },
          sapMaterialNumber: {
            type: 'string',
            description:
              'SAP material number. Usually server-side SAP enrichment; omit unless explicitly shown in the source document.',
          },
          supplierMaterialNumber: {
            type: 'string',
            description:
              'Supplier/product material number when separate from the selected articleNumber.',
          },
          customerMaterialNumber: {
            type: 'string',
            description:
              'Customer material number / Ihre Material-Nr. when shown separately from the supplier material number.',
          },
          customerArticleNumber: {
            type: 'string',
            description:
              'Customer article number from a customer order. For customer_order documents this is the primary customer-side product identifier used for KNMT/customer material lookup.',
          },
          orderNumber: {
            type: 'string',
            description:
              'Purchase order / Bestellnummer that applies to this line item, especially when a long invoice contains multiple orders.',
          },
          orderDate: {
            type: 'string',
            description: 'Order date in YYYY-MM-DD that applies to this line item.',
          },
          deliveryNote: {
            type: 'string',
            description: 'Delivery note / Lieferschein number that applies to this line item.',
          },
          deliveryDate: {
            type: 'string',
            description: 'Delivery or service date in YYYY-MM-DD that applies to this line item.',
          },
          deliveryAddress: {
            type: 'string',
            description:
              'Warenempfaenger / delivery address text that applies to this line item when multiple delivery blocks are present.',
          },
          description: {
            type: 'string',
            description:
              'Line item description / Artikel-Bezeichnung. Include freight, packing, handling, Verpackungskosten, Versandkosten, Lieferkosten, Sonderkosten, Nebenkosten, service fees, or surcharge rows as normal line items when they contribute to the total.',
          },
          quantity: { type: 'number', description: 'Line item quantity / Menge.' },
          unit: { type: 'string', description: 'Quantity unit / Me.' },
          unitPrice: {
            type: 'number',
            description: 'Single-unit price or listed price per base quantity.',
          },
          pricePer: {
            type: 'number',
            description:
              'Base quantity for the listed price. Use 1 when the invoice shows a normal single-unit price.',
          },
          marginPercent: {
            type: 'number',
            description:
              'Margin / Spanne in percent when explicitly printed or unambiguously requested on a customer order line.',
          },
          marginAmount: {
            type: 'number',
            description:
              'Margin / Spanne amount when explicitly printed or unambiguously requested on a customer order line.',
          },
          discountPercent: {
            type: 'number',
            description: 'Line discount / Rabatt / Zu- oder Abschlag in percent when shown.',
          },
          discountAmount: {
            type: 'number',
            description: 'Line discount / Rabatt / Zu- oder Abschlag amount when shown.',
          },
          netAmount: { type: 'number', description: 'Net line amount.' },
          vatRate: { type: 'number', description: 'VAT rate for the line item.' },
        },
      },
    },
    totals: {
      type: 'object',
      properties: {
        netAmount: { type: 'number', description: 'Invoice net total.' },
        vatAmount: {
          type: 'number',
          description: 'Invoice VAT total / MwSt-Betrag / Mehrwertsteuer / Umsatzsteuer.',
        },
        grossAmount: { type: 'number', description: 'Invoice gross total / Rechnungsbetrag.' },
      },
    },
    raw: {
      type: 'object',
      properties: {
        extractedAttributes: {
          type: 'object',
          properties: {
            customerNumber: {
              type: 'string',
              description: 'Customer number if shown elsewhere on the invoice.',
            },
            orderNumber: { type: 'string', description: 'Own purchase order / Bestellnummer.' },
            orderDate: {
              type: 'string',
              description:
                'Order date / Datum der Bestellung in YYYY-MM-DD. If only a calendar week is shown, convert it to the Monday of that week. If no separate order date exists but the document date clearly serves as order date, reuse the document date.',
            },
            customerPoNumber: {
              type: 'string',
              description:
                'Customer purchase order number / Kundenbestellnummer for customer_order documents. Also mirror it to orderNumber when this is the primary customer PO.',
            },
            customerPoDate: {
              type: 'string',
              description:
                'Customer purchase order date / Kundenbestelldatum in YYYY-MM-DD for customer_order documents. Also mirror it to orderDate when this is the primary customer PO date.',
            },
            salesOrderType: {
              type: 'string',
              description:
                'SAP sales order type when shown or specified by the process. For customer_order defaulting is done server-side to ZAN1.',
            },
            salesOrganization: {
              type: 'string',
              description: 'SAP sales organization when shown on a customer order.',
            },
            distributionChannel: {
              type: 'string',
              description: 'SAP distribution channel when shown on a customer order.',
            },
            division: {
              type: 'string',
              description: 'SAP division when shown on a customer order.',
            },
            shippingPlant: {
              type: 'string',
              description:
                'SAP delivery/shipping plant. For customer_order defaulting is done server-side to 1000 unless clearly printed otherwise.',
            },
            billingBlock: {
              type: 'string',
              description: 'SAP billing block / Fakturasperre code or text when requested.',
            },
            deliveryBlock: {
              type: 'string',
              description: 'SAP delivery block / Liefersperre code or text when requested.',
            },
            requestedDeliveryDate: {
              type: 'string',
              description:
                'Requested delivery date / Wunschliefertermin in YYYY-MM-DD. If only a calendar week is shown, convert it to the Monday of that week.',
            },
            purchaseOrders: {
              type: 'array',
              description:
                'All purchase orders visible in a long or multi-delivery invoice. Use one entry per Bestellnummer.',
              items: {
                type: 'object',
                properties: {
                  orderNumber: { type: 'string', description: 'Purchase order number.' },
                  orderDate: { type: 'string', description: 'Purchase order date in YYYY-MM-DD.' },
                  deliveryDate: {
                    type: 'string',
                    description: 'Delivery/service date for this purchase order in YYYY-MM-DD.',
                  },
                  deliveryAddress: {
                    type: 'string',
                    description: 'Delivery recipient/address for this purchase order.',
                  },
                },
              },
            },
            orderConfirmationNumber: {
              type: 'string',
              description:
                'Order confirmation number / Auftragsbestätigungsnummer when separate from invoice.number.',
            },
            billingAddress: {
              type: 'string',
              description: 'Billing address text when different from the shipping address.',
            },
            deliveryNote: { type: 'string', description: 'Delivery note number / Lieferschein.' },
            deliveryNotes: {
              type: 'array',
              description:
                'All delivery notes visible in a long or multi-delivery invoice. Use one entry per Lieferschein.',
              items: {
                type: 'object',
                properties: {
                  deliveryNote: { type: 'string', description: 'Delivery note number.' },
                  orderNumber: {
                    type: 'string',
                    description: 'Purchase order number linked to this delivery note.',
                  },
                  deliveryDate: {
                    type: 'string',
                    description: 'Delivery/service date for this delivery note in YYYY-MM-DD.',
                  },
                  deliveryAddress: {
                    type: 'string',
                    description: 'Delivery recipient/address for this delivery note.',
                  },
                  shippingMethod: { type: 'string', description: 'Shipping method.' },
                },
              },
            },
            deliveryDate: {
              type: 'string',
              description:
                'Delivery date in YYYY-MM-DD. If only a calendar week such as KW 35/2025 is printed, convert it to the Monday of that calendar week.',
            },
            shippingMethod: { type: 'string', description: 'Shipping or pickup method.' },
            deliveryAddress: {
              type: 'string',
              description:
                'Shipping or delivery address text if separate from the billing address. If the document only says siehe Rechnungsanschrift / see invoice address, include the resolved full invoice-recipient address text or omit this field.',
            },
            incoterms: {
              type: 'string',
              description: 'Incoterms text such as EXW, FCA, DAP, or similar.',
            },
            processedBy: { type: 'string', description: 'Processed by / Bearbeitet.' },
            processedByPhone: { type: 'string', description: 'Processed by phone number.' },
            processedByEmail: { type: 'string', description: 'Processed by email address.' },
            vatBase: { type: 'number', description: 'VAT base / taxable amount.' },
            vatRate: { type: 'number', description: 'Overall VAT rate if shown explicitly.' },
            vatAmount: { type: 'number', description: 'Overall VAT amount.' },
            skontoDeadline: {
              type: 'string',
              description:
                'Skonto deadline in YYYY-MM-DD. Calculate it when the document gives only relative terms such as 10 Tage Skonto.',
            },
            skontoPercent: { type: 'number', description: 'Skonto percentage.' },
            skontoAmount: {
              type: 'number',
              description:
                'Skonto amount. Calculate it from the gross amount and skonto percentage when the document gives enough information but omits the printed amount.',
            },
            amountAfterSkonto: {
              type: 'number',
              description:
                'Gross amount after skonto deduction. Calculate it when gross amount and skonto amount or skonto percentage are available.',
            },
            withoutDeductionDueDate: {
              type: 'string',
              description: 'Deadline for payment without deduction / Ohne Abzug bis in YYYY-MM-DD.',
            },
            paymentDueDate: {
              type: 'string',
              description: 'Final payment due date in YYYY-MM-DD.',
            },
            registryCourt: { type: 'string', description: 'Registry court text.' },
            generalPartner: { type: 'string', description: 'General partner entity text.' },
            managingDirectors: { type: 'string', description: 'Managing directors text.' },
            pdfChunks: {
              type: 'array',
              description:
                'Leave empty. The server populates this with long-PDF chunk ranges used during extraction.',
              items: {
                type: 'object',
                properties: {
                  kind: {
                    type: 'string',
                    enum: ['header', 'positions', 'summary', 'appendix'],
                  },
                  startPage: { type: 'number' },
                  endPage: { type: 'number' },
                  contextStartPage: { type: 'number' },
                  contextEndPage: { type: 'number' },
                },
              },
            },
            missingFields: {
              type: 'array',
              items: { type: 'string' },
              description:
                'Leave empty. The server populates this with mandatory fields that were not found.',
            },
          },
        },
      },
    },
  },
} as const;

/**
 * Canonical invoice payload. Both pipeline paths (structured XML and LLM)
 * converge on this shape; downstream validation, persistence and UI work
 * against `InvoiceBlob` regardless of source (ADR-0002).
 */
const InvoiceBlobObjectSchema = z.object({
  meta: z.object({
    format: InvoiceFormatEnum,
    extractionMethod: ExtractionMethodEnum,
    extractedAt: z.string().datetime(),
    modelVersion: z.string().optional(),
    confidence: z.number().min(0).max(1).optional(),
  }),
  documentType: InvoiceDocumentTypeEnum.optional(),
  invoice: z.object({
    number: z.string().min(1),
    issueDate: z.string().date(),
    dueDate: z.string().date().optional(),
    deliveryDate: z.string().date().optional(),
    currency: z.string().length(3).default('EUR'),
  }),
  seller: SellerSchema,
  buyer: BuyerSchema,
  lineItems: z.array(LineItemSchema),
  totals: TotalsSchema,
  paymentTerms: z.string().optional(),
  /** Pipeline-internal payload (raw XML as JSON, raw LLM JSON, etc.). */
  raw: RawInvoicePayloadSchema.optional(),
});
export const InvoiceBlobSchema = z.preprocess(normaliseInvoiceBlobInput, InvoiceBlobObjectSchema);
export type InvoiceBlob = z.infer<typeof InvoiceBlobSchema>;
